import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Smartphone, Search, Check, X, ExternalLink, Loader2 } from 'lucide-react';
import { useESIMDevices } from '@/hooks/useESIMDevices';

interface DeviceModel {
  name: string;
  supported: boolean;
  notes?: string;
}

interface DeviceBrand {
  brand: string;
  models: DeviceModel[];
}

export function ESIMDeviceCompatibilityModal() {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const { deviceData: dbDevices, loading: dbLoading } = useESIMDevices();

  // Static fallback list - used when database is empty
  const staticDeviceData: DeviceBrand[] = [
    {
      brand: 'Apple iPhone',
      models: [
        { name: 'iPhone Air', supported: true, notes: 'First globally eSIM-only iPhone' },
        { name: 'iPhone 17 Pro Max', supported: true },
        { name: 'iPhone 17 Pro', supported: true },
        { name: 'iPhone 17', supported: true },
        { name: 'iPhone 16e', supported: true },
        { name: 'iPhone 16 Pro Max', supported: true },
        { name: 'iPhone 16 Pro', supported: true },
        { name: 'iPhone 16 Plus', supported: true },
        { name: 'iPhone 16', supported: true },
        { name: 'iPhone 15 Pro Max', supported: true },
        { name: 'iPhone 15 Pro', supported: true },
        { name: 'iPhone 15 Plus', supported: true },
        { name: 'iPhone 15', supported: true },
        { name: 'iPhone 14 Pro Max', supported: true },
        { name: 'iPhone 14 Pro', supported: true },
        { name: 'iPhone 14 Plus', supported: true },
        { name: 'iPhone 14', supported: true },
        { name: 'iPhone 13 Pro Max', supported: true },
        { name: 'iPhone 13 Pro', supported: true },
        { name: 'iPhone 13 Mini', supported: true },
        { name: 'iPhone 13', supported: true },
        { name: 'iPhone 12 Pro Max', supported: true },
        { name: 'iPhone 12 Pro', supported: true },
        { name: 'iPhone 12 Mini', supported: true },
        { name: 'iPhone 12', supported: true },
        { name: 'iPhone 11 Pro Max', supported: true },
        { name: 'iPhone 11 Pro', supported: true },
        { name: 'iPhone 11', supported: true },
        { name: 'iPhone XS Max', supported: true },
        { name: 'iPhone XS', supported: true },
        { name: 'iPhone XR', supported: true },
        { name: 'iPhone SE 3 (2022)', supported: true },
        { name: 'iPhone SE 2 (2020)', supported: true },
        { name: 'iPhone X and older', supported: false },
      ]
    },
    {
      brand: 'Apple iPad',
      models: [
        { name: 'iPad Pro 13-inch (M4, 2024)', supported: true },
        { name: 'iPad Pro 11-inch (M4, 2024)', supported: true },
        { name: 'iPad Air 13-inch (M2, 2024)', supported: true },
        { name: 'iPad Air 11-inch (M2, 2024)', supported: true },
        { name: 'iPad 10th generation (2022)', supported: true },
        { name: 'iPad Pro 12.9″ (2020, A2069)', supported: true },
        { name: 'iPad Pro 11″ (2020, A2068)', supported: true },
        { name: 'iPad Air (2019, A2123)', supported: true },
        { name: 'iPad (2019, A2198)', supported: true },
        { name: 'iPad Mini (2019, A2124)', supported: true },
      ]
    },
    {
      brand: 'Apple Watch',
      models: [
        { name: 'Apple Watch Ultra 2', supported: true },
        { name: 'Apple Watch Ultra', supported: true },
        { name: 'Apple Watch Series 10', supported: true },
        { name: 'Apple Watch Series 9', supported: true },
        { name: 'Apple Watch Series 8', supported: true },
        { name: 'Apple Watch Series 7', supported: true },
        { name: 'Apple Watch Series 6', supported: true },
        { name: 'Apple Watch SE (2nd Gen)', supported: true },
        { name: 'Apple Watch SE (1st Gen)', supported: true },
        { name: 'Apple Watch Series 5', supported: true },
        { name: 'Apple Watch Series 4', supported: true },
        { name: 'Apple Watch Series 3 (GPS + Cellular)', supported: true },
      ]
    },
    {
      brand: 'Samsung Galaxy S Series',
      models: [
        { name: 'Galaxy S25 Ultra', supported: true },
        { name: 'Galaxy S25+', supported: true },
        { name: 'Galaxy S25', supported: true },
        { name: 'Galaxy S25 Edge', supported: true },
        { name: 'Galaxy S25 FE', supported: true },
        { name: 'Galaxy S24 Ultra', supported: true },
        { name: 'Galaxy S24+', supported: true },
        { name: 'Galaxy S24', supported: true },
        { name: 'Galaxy S24 FE', supported: true },
        { name: 'Galaxy S23 Ultra', supported: true },
        { name: 'Galaxy S23+', supported: true },
        { name: 'Galaxy S23', supported: true },
        { name: 'Galaxy S23 FE', supported: true, notes: 'China/Hong Kong models not supported' },
        { name: 'Galaxy S22 Ultra', supported: true },
        { name: 'Galaxy S22+', supported: true },
        { name: 'Galaxy S22', supported: true },
        { name: 'Galaxy S21+ 5G', supported: true },
        { name: 'Galaxy S21 Ultra 5G', supported: true },
        { name: 'Galaxy S21', supported: true },
        { name: 'Galaxy S20 Ultra 5G', supported: true },
        { name: 'Galaxy S20+ 5G', supported: true },
        { name: 'Galaxy S20+', supported: true },
        { name: 'Galaxy S20', supported: true },
        { name: 'Galaxy S20 FE', supported: false, notes: 'Not eSIM compatible' },
        { name: 'Galaxy S10 and older', supported: false },
      ]
    },
    {
      brand: 'Samsung Galaxy Z Foldables',
      models: [
        { name: 'Galaxy Z Fold7', supported: true },
        { name: 'Galaxy Z Flip7', supported: true },
        { name: 'Galaxy Z Flip7 FE', supported: true },
        { name: 'Galaxy Z Fold6 5G', supported: true },
        { name: 'Galaxy Z Flip6 5G', supported: true },
        { name: 'Galaxy Z Fold5 5G', supported: true },
        { name: 'Galaxy Z Flip5 5G', supported: true },
        { name: 'Galaxy Z Fold4', supported: true },
        { name: 'Galaxy Z Flip4', supported: true },
        { name: 'Galaxy Z Fold3 5G', supported: true },
        { name: 'Galaxy Z Flip3 5G', supported: true },
        { name: 'Galaxy Z Fold2 5G', supported: true },
        { name: 'Galaxy Z Flip 5G', supported: true },
        { name: 'Galaxy Z Flip', supported: true },
        { name: 'Galaxy Fold', supported: true },
      ]
    },
    {
      brand: 'Samsung Galaxy A Series',
      models: [
        { name: 'Galaxy A56', supported: true },
        { name: 'Galaxy A55 5G', supported: true },
        { name: 'Galaxy A54 5G', supported: true, notes: 'Select models only' },
        { name: 'Galaxy A36', supported: true },
        { name: 'Galaxy A35', supported: true },
      ]
    },
    {
      brand: 'Samsung Galaxy Note',
      models: [
        { name: 'Galaxy Note 20 Ultra 5G', supported: true },
        { name: 'Galaxy Note 20', supported: true },
      ]
    },
    {
      brand: 'Samsung Galaxy Watch',
      models: [
        { name: 'Galaxy Watch Ultra', supported: true },
        { name: 'Galaxy Watch 7', supported: true },
        { name: 'Galaxy Watch FE', supported: true },
        { name: 'Galaxy Watch 6 Classic', supported: true },
        { name: 'Galaxy Watch 6', supported: true },
        { name: 'Galaxy Watch 5 Pro', supported: true },
        { name: 'Galaxy Watch 5', supported: true },
        { name: 'Galaxy Watch 4 Classic', supported: true },
        { name: 'Galaxy Watch 4', supported: true },
      ]
    },
    {
      brand: 'Google Pixel',
      models: [
        { name: 'Pixel 10 Pro XL', supported: true },
        { name: 'Pixel 10 Pro', supported: true },
        { name: 'Pixel 10', supported: true },
        { name: 'Pixel 9 Pro XL', supported: true },
        { name: 'Pixel 9 Pro Fold', supported: true },
        { name: 'Pixel 9 Pro', supported: true },
        { name: 'Pixel 9', supported: true },
        { name: 'Pixel 8a', supported: true },
        { name: 'Pixel 8 Pro', supported: true },
        { name: 'Pixel 8', supported: true },
        { name: 'Pixel Fold', supported: true },
        { name: 'Pixel 7a', supported: true },
        { name: 'Pixel 7 Pro', supported: true },
        { name: 'Pixel 7', supported: true },
        { name: 'Pixel 6a', supported: true },
        { name: 'Pixel 6 Pro', supported: true },
        { name: 'Pixel 6', supported: true },
        { name: 'Pixel 5a', supported: true },
        { name: 'Pixel 5', supported: true },
        { name: 'Pixel 4a', supported: true },
        { name: 'Pixel 4 XL', supported: true },
        { name: 'Pixel 4', supported: true },
        { name: 'Pixel 3a XL', supported: true },
        { name: 'Pixel 3a', supported: true, notes: 'Not SE Asia models' },
        { name: 'Pixel 3 XL', supported: true },
        { name: 'Pixel 3', supported: true, notes: 'Not AU/Taiwan/Japan models' },
        { name: 'Pixel 2 XL', supported: true },
        { name: 'Pixel 2', supported: true, notes: 'Google Fi only' },
      ]
    },
    {
      brand: 'Google Pixel Watch',
      models: [
        { name: 'Pixel Watch 3', supported: true },
        { name: 'Pixel Watch 2', supported: true },
        { name: 'Pixel Watch', supported: true },
      ]
    },
    {
      brand: 'OnePlus',
      models: [
        { name: 'OnePlus 15', supported: true },
        { name: 'OnePlus 13T', supported: true },
        { name: 'OnePlus 13R', supported: true },
        { name: 'OnePlus 13', supported: true },
        { name: 'OnePlus 12', supported: true },
        { name: 'OnePlus Open', supported: true },
        { name: 'OnePlus 11', supported: true },
      ]
    },
    {
      brand: 'Xiaomi',
      models: [
        { name: 'Xiaomi 15 Ultra', supported: true },
        { name: 'Xiaomi 15T Pro', supported: true },
        { name: 'Xiaomi 15T', supported: true },
        { name: 'Xiaomi 15', supported: true },
        { name: 'Xiaomi 14T Pro', supported: true },
        { name: 'Xiaomi 14T', supported: true },
        { name: 'Xiaomi 14 Pro', supported: true },
        { name: 'Xiaomi 14', supported: true },
        { name: 'Xiaomi 13T Pro', supported: true },
        { name: 'Xiaomi 13T', supported: true },
        { name: 'Xiaomi 13 Pro', supported: true },
        { name: 'Xiaomi 13', supported: true },
        { name: 'Xiaomi 13 Lite', supported: true },
        { name: 'Xiaomi 12T Pro', supported: true },
        { name: 'Xiaomi 12T', supported: false, notes: 'Only 12T Pro supported' },
        { name: 'Xiaomi Poco X7', supported: true },
        { name: 'Redmi Note 14 Pro+', supported: true },
        { name: 'Redmi Note 14 Pro', supported: true },
        { name: 'Redmi Note 13 Pro+', supported: true },
      ]
    },
    {
      brand: 'Oppo',
      models: [
        { name: 'Oppo Find X9 Pro', supported: true },
        { name: 'Oppo Find X9', supported: true },
        { name: 'Oppo Find X8 Pro', supported: true },
        { name: 'Oppo Find X8', supported: true },
        { name: 'Oppo Find X5 Pro', supported: true },
        { name: 'Oppo Find X5', supported: true },
        { name: 'Oppo Find X3 Pro', supported: true },
        { name: 'Oppo Find X3', supported: true },
        { name: 'Oppo Find N3 Flip', supported: true },
        { name: 'Oppo Find N3', supported: true },
        { name: 'Oppo Find N2 Flip', supported: true },
        { name: 'Oppo Reno14 Pro', supported: true },
        { name: 'Oppo Reno14', supported: true },
        { name: 'Oppo Reno 9A', supported: true },
        { name: 'Oppo Reno 6 Pro 5G', supported: true },
        { name: 'Oppo Reno 5A', supported: true },
        { name: 'Oppo A55s 5G', supported: true },
      ]
    },
    {
      brand: 'Huawei',
      models: [
        { name: 'Huawei Pura 70 Pro', supported: true },
        { name: 'Huawei Mate 40 Pro', supported: true },
        { name: 'Huawei P40 Pro', supported: true },
        { name: 'Huawei P40', supported: true },
        { name: 'Huawei P40 Pro+', supported: false, notes: 'Not compatible' },
        { name: 'Huawei P50 Pro', supported: false, notes: 'Not compatible' },
      ]
    },
    {
      brand: 'Motorola',
      models: [
        { name: 'Motorola Razr 60 Ultra', supported: true },
        { name: 'Motorola Razr 60', supported: true },
        { name: 'Motorola Razr+ 2024', supported: true },
        { name: 'Motorola Razr 2024', supported: true },
        { name: 'Motorola Razr 40 Ultra', supported: true },
        { name: 'Motorola Razr 40', supported: true },
        { name: 'Motorola Razr+ 2023', supported: true },
        { name: 'Motorola Razr 2022', supported: true },
        { name: 'Motorola Razr 5G', supported: true },
        { name: 'Motorola Razr 2019', supported: true },
        { name: 'Motorola Edge 60 Pro', supported: true },
        { name: 'Motorola Edge 60 Fusion', supported: true },
        { name: 'Motorola Edge 60 Stylus', supported: true },
        { name: 'Motorola Edge 60', supported: true },
        { name: 'Motorola Edge 50 Ultra', supported: true },
        { name: 'Motorola Edge 50 Pro', supported: true },
        { name: 'Motorola Edge 50 Fusion', supported: true },
        { name: 'Motorola Edge+ (2024)', supported: true },
        { name: 'Motorola Edge+ (2023)', supported: true },
        { name: 'Motorola Edge 40 Pro', supported: true },
        { name: 'Motorola Edge 40 Neo', supported: true },
        { name: 'Motorola Edge 40', supported: true },
        { name: 'Motorola Edge 2023', supported: true },
        { name: 'Motorola Edge 2022', supported: true },
        { name: 'Motorola Moto G Stylus 5G (2024)', supported: true },
        { name: 'Motorola Moto G Power 5G (2024)', supported: true },
        { name: 'Motorola G84', supported: true },
        { name: 'Motorola G54 5G', supported: true },
        { name: 'Motorola G53J 5G', supported: true },
        { name: 'Motorola G52J 5G II', supported: true },
        { name: 'Motorola G52J 5G', supported: true },
        { name: 'Motorola Moto G35', supported: true },
        { name: 'Motorola Moto G34', supported: true },
      ]
    },
    {
      brand: 'Sony Xperia',
      models: [
        { name: 'Sony Xperia 1 VI', supported: true },
        { name: 'Sony Xperia 1 V', supported: true },
        { name: 'Sony Xperia 1 IV', supported: true },
        { name: 'Sony Xperia 5 V', supported: true },
        { name: 'Sony Xperia 5 IV', supported: true },
        { name: 'Sony Xperia 10 VI', supported: true },
        { name: 'Sony Xperia 10 V', supported: true },
        { name: 'Sony Xperia 10 IV', supported: true },
        { name: 'Sony Xperia 10 III Lite', supported: true },
        { name: 'Sony Xperia Ace III', supported: true },
      ]
    },
    {
      brand: 'Honor',
      models: [
        { name: 'Honor Magic8 Pro', supported: true },
        { name: 'Honor Magic 6 Pro', supported: true },
        { name: 'Honor Magic 5 Pro', supported: true },
        { name: 'Honor Magic 4 Pro', supported: true },
        { name: 'Honor Magic V3', supported: true },
        { name: 'Honor Magic V2', supported: true },
        { name: 'Honor 400 Lite', supported: true },
        { name: 'Honor 200 Pro', supported: true },
        { name: 'Honor 90', supported: true },
        { name: 'Honor X8', supported: true },
      ]
    },
    {
      brand: 'Vivo',
      models: [
        { name: 'Vivo X300 Pro', supported: true },
        { name: 'Vivo X300', supported: true },
        { name: 'Vivo X200s', supported: true },
        { name: 'Vivo X200 Pro', supported: true },
        { name: 'Vivo X200 FE', supported: true },
        { name: 'Vivo X200', supported: true },
        { name: 'Vivo X100 Pro', supported: true },
        { name: 'Vivo X90 Pro', supported: true },
        { name: 'Vivo X80 Pro', supported: true },
        { name: 'Vivo V40', supported: true },
        { name: 'Vivo V40 Lite', supported: true },
        { name: 'Vivo V40 SE', supported: true },
        { name: 'Vivo V29', supported: true },
        { name: 'Vivo V29 Lite', supported: true },
        { name: 'Vivo V29 Lite 5G', supported: true, notes: 'Europe only' },
      ]
    },
    {
      brand: 'Realme',
      models: [
        { name: 'Realme GT 8 Pro', supported: true },
        { name: 'Realme GT 7', supported: true },
        { name: 'Realme 14 Pro+', supported: true },
      ]
    },
    {
      brand: 'Asus',
      models: [
        { name: 'Asus ROG Phone 9 Pro', supported: true },
        { name: 'Asus ROG Phone 9', supported: true },
        { name: 'Asus Zenfone 12 Ultra', supported: true },
      ]
    },
    {
      brand: 'Sharp',
      models: [
        { name: 'Sharp AQUOS R8 Pro', supported: true },
        { name: 'Sharp AQUOS R8', supported: true },
        { name: 'Sharp AQUOS R7', supported: true },
        { name: 'Sharp AQUOS sense8', supported: true },
        { name: 'Sharp AQUOS sense7 plus', supported: true },
        { name: 'Sharp AQUOS sense7', supported: true },
        { name: 'Sharp AQUOS Sense6s', supported: true },
        { name: 'Sharp AQUOS sense4 lite', supported: true },
        { name: 'Sharp AQUOS wish3', supported: true },
        { name: 'Sharp AQUOS wish2', supported: true },
        { name: 'Sharp AQUOS Wish', supported: true },
        { name: 'Sharp AQUOS zero 6', supported: true },
        { name: 'Simple Sumaho6', supported: true },
      ]
    },
    {
      brand: 'Rakuten',
      models: [
        { name: 'Rakuten Hand 5G', supported: true },
        { name: 'Rakuten Hand', supported: true },
        { name: 'Rakuten Big-S', supported: true },
        { name: 'Rakuten Big', supported: true },
        { name: 'Rakuten Mini', supported: true },
      ]
    },
    {
      brand: 'Nothing',
      models: [
        { name: 'Nothing Phone 3a Pro', supported: true },
        { name: 'Nothing Phone 3', supported: true },
        { name: 'Nothing Phone (2a) Plus', supported: true },
        { name: 'Nothing Phone (2a)', supported: true },
        { name: 'Nothing Phone (2)', supported: true },
        { name: 'Nothing Phone (1)', supported: true },
      ]
    },
    {
      brand: 'Fairphone',
      models: [
        { name: 'Fairphone 5', supported: true },
        { name: 'Fairphone 4', supported: true },
      ]
    },
    {
      brand: 'Nokia',
      models: [
        { name: 'Nokia XR21', supported: true },
        { name: 'Nokia X30', supported: true },
        { name: 'Nokia G60 5G', supported: true },
      ]
    },
    {
      brand: 'TCL',
      models: [
        { name: 'TCL 50 5G', supported: true },
      ]
    },
    {
      brand: 'ZTE',
      models: [
        { name: 'ZTE Nubia Flip', supported: true },
      ]
    },
    {
      brand: 'Other Brands',
      models: [
        { name: 'Gemini PDA', supported: true },
        { name: 'DOOGEE V30', supported: true },
        { name: 'HAMMER Blade 5G', supported: true },
        { name: 'HAMMER Blade 3', supported: true },
        { name: 'HAMMER Explorer PRO', supported: true },
        { name: 'myPhone NOW eSIM', supported: true },
        { name: 'OUKITEL WP33 Pro', supported: true },
        { name: 'OUKITEL WP30 Pro', supported: true },
        { name: 'Nuu X5', supported: true },
      ]
    },
  ];

  // Use database data if available, otherwise use static fallback
  const deviceData: DeviceBrand[] = dbDevices.length > 0 
    ? dbDevices.map(d => ({ brand: d.name, models: d.models }))
    : staticDeviceData;

  const filteredData = deviceData.map(brand => ({
    ...brand,
    models: brand.models.filter(model =>
      searchQuery === '' || 
      model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      brand.brand.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(brand => brand.models.length > 0);

  const supportedDevices = deviceData.flatMap(brand => 
    brand.models.filter(m => m.supported)
  );
  const totalSupported = supportedDevices.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="h-auto p-0 text-sm text-blue-800 hover:text-blue-600 font-semibold underline decoration-2 underline-offset-4 hover:decoration-blue-600 transition-colors flex items-center gap-1.5">
          Check device compatibility
          <Smartphone className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Smartphone className="h-6 w-6" />
            eSIM Device Compatibility
          </DialogTitle>
          <DialogDescription className="text-base">
            Check if your device supports eSIM technology. {totalSupported}+ compatible devices listed.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by brand or model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4 w-full justify-start overflow-x-auto flex-wrap h-auto gap-2">
              <TabsTrigger value="all">All Brands</TabsTrigger>
              <TabsTrigger value="apple">Apple</TabsTrigger>
              <TabsTrigger value="samsung">Samsung</TabsTrigger>
              <TabsTrigger value="google">Google</TabsTrigger>
              <TabsTrigger value="others">Others</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-6 mt-4">
              {filteredData.map((brand) => (
                <div key={brand.brand} className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">{brand.brand}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {brand.models.map((model) => (
                      <div
                        key={model.name}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          model.supported
                            ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
                            : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {model.supported ? (
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <span className="text-sm font-medium">{model.name}</span>
                            {model.notes && (
                              <p className="text-xs text-muted-foreground mt-0.5">{model.notes}</p>
                            )}
                          </div>
                        </div>
                        <Badge variant={model.supported ? "default" : "destructive"} className="ml-2">
                          {model.supported ? 'Supported' : 'Not Supported'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="apple" className="space-y-6 mt-4">
              {filteredData.filter(b => b.brand.includes('Apple')).map((brand) => (
                <div key={brand.brand} className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">{brand.brand}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {brand.models.map((model) => (
                      <div
                        key={model.name}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          model.supported
                            ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
                            : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {model.supported ? (
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium">{model.name}</span>
                        </div>
                        <Badge variant={model.supported ? "default" : "destructive"} className="ml-2">
                          {model.supported ? 'Supported' : 'Not Supported'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="samsung" className="space-y-6 mt-4">
              {filteredData.filter(b => b.brand.includes('Samsung')).map((brand) => (
                <div key={brand.brand} className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">{brand.brand}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {brand.models.map((model) => (
                      <div
                        key={model.name}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          model.supported
                            ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
                            : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {model.supported ? (
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <span className="text-sm font-medium">{model.name}</span>
                            {model.notes && (
                              <p className="text-xs text-muted-foreground mt-0.5">{model.notes}</p>
                            )}
                          </div>
                        </div>
                        <Badge variant={model.supported ? "default" : "destructive"} className="ml-2">
                          {model.supported ? 'Supported' : 'Not Supported'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="google" className="space-y-6 mt-4">
              {filteredData.filter(b => b.brand.includes('Google')).map((brand) => (
                <div key={brand.brand} className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">{brand.brand}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {brand.models.map((model) => (
                      <div
                        key={model.name}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          model.supported
                            ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
                            : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {model.supported ? (
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium">{model.name}</span>
                        </div>
                        <Badge variant={model.supported ? "default" : "destructive"} className="ml-2">
                          {model.supported ? 'Supported' : 'Not Supported'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="others" className="space-y-6 mt-4">
              {filteredData.filter(b => !b.brand.includes('Apple') && !b.brand.includes('Samsung') && !b.brand.includes('Google')).map((brand) => (
                <div key={brand.brand} className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">{brand.brand}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {brand.models.map((model) => (
                      <div
                        key={model.name}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          model.supported
                            ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
                            : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {model.supported ? (
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <span className="text-sm font-medium">{model.name}</span>
                            {model.notes && (
                              <p className="text-xs text-muted-foreground mt-0.5">{model.notes}</p>
                            )}
                          </div>
                        </div>
                        <Badge variant={model.supported ? "default" : "destructive"} className="ml-2">
                          {model.supported ? 'Supported' : 'Not Supported'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        <div className="px-6 py-4 border-t bg-muted/30 flex-shrink-0">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <ExternalLink className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              This list is regularly updated with new device releases. If you don't see your device, check your phone's settings under 
              <span className="font-semibold"> Settings → Cellular/Mobile → Add eSIM</span> or contact your device manufacturer.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
